import api from "../../utils/api";
import { LOGOUT } from "../types";

export const mine = ({ directoryUuid = null } = {}) => async (dispatch) => {
    try {
        const query = directoryUuid ? `?directoryUuid=${directoryUuid}` : "";
        const res = await api.get(`/files/me${query}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false, items: [], directories: [], files: [] };
    }
};

export const sharedWithMe = () => async (dispatch) => {
    try {
        const res = await api.get("/files/shared-with-me");
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false, items: [], directories: [], files: [] };
    }
};

const scopePath = ({ ownerType, ownerUuid, contextType, contextUuid }) => {
    let url = `/files/o/${ownerType}/${ownerUuid}`;

    if (contextType && contextUuid) {
        url += `/c/${contextType}/${contextUuid}`;
    }

    return url;
};

export const list = ({ ownerType, ownerUuid, contextType = null, contextUuid = null, directoryUuid = null }) => async (dispatch) => {
    try {
        const query = directoryUuid ? `?directoryUuid=${directoryUuid}` : "";
        const res = await api.get(`${scopePath({ ownerType, ownerUuid, contextType, contextUuid })}${query}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false, items: [], directories: [], files: [] };
    }
};

export const directories = ({ ownerType, ownerUuid, contextType = null, contextUuid = null }) => async (dispatch) => {
    try {
        const res = await api.get(`${scopePath({ ownerType, ownerUuid, contextType, contextUuid })}/directories`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false, items: [] };
    }
};

export const createDirectory = ({ ownerType, ownerUuid, contextType = null, contextUuid = null, parentUuid = null, name }) => async (dispatch) => {
    try {
        const res = await api.post(`${scopePath({ ownerType, ownerUuid, contextType, contextUuid })}/directory`, {
            parentUuid,
            name
        });
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};

export const download = ({ uuid, name }) => async (dispatch) => {
    try {
        const res = await api.get(`/files/i/${uuid}/download`, {
            responseType: "blob"
        });

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name || "download");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return true;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return false;
    }
};

export const downloadMany = ({ files = [], directories = [], name = "files.zip" }) => async (dispatch) => {
    try {
        const res = await api.post("/files/download", { files, directories }, {
            responseType: "blob"
        });

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return true;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return false;
    }
};

export const downloadContents = ({
    ownerType,
    ownerUuid,
    contextType = null,
    contextUuid = null,
    directoryUuid = null,
    name = "folder-contents.zip"
}) => async (dispatch) => {
    try {
        const query = directoryUuid ? `?directoryUuid=${directoryUuid}` : "";
        const res = await api.get(`${scopePath({ ownerType, ownerUuid, contextType, contextUuid })}/download${query}`, {
            responseType: "blob"
        });

        const url = window.URL.createObjectURL(new Blob([res.data], {
            type: res.headers["content-type"] || "application/zip"
        }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return true;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return false;
    }
};

export const renameFile = ({ uuid, name }) => async (dispatch) => {
    try {
        const res = await api.put(`/files/i/${uuid}`, { name });
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};

export const renameDirectory = ({ uuid, name }) => async (dispatch) => {
    try {
        const res = await api.put(`/files/directories/i/${uuid}`, { name });
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};

export const moveFile = ({ uuid, directoryUuid = null }) => async (dispatch) => {
    try {
        const res = await api.put(`/files/i/${uuid}/move`, { directoryUuid });
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};

export const moveDirectory = ({ uuid, directoryUuid = null }) => async (dispatch) => {
    try {
        const res = await api.put(`/files/directories/i/${uuid}/move`, { directoryUuid });
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};

export const deleteFile = ({ uuid }) => async (dispatch) => {
    try {
        const res = await api.delete(`/files/i/${uuid}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};

export const deleteDirectory = ({ uuid }) => async (dispatch) => {
    try {
        const res = await api.delete(`/files/directories/i/${uuid}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) {
            dispatch({ type: LOGOUT });
        }
        return { success: false };
    }
};
